import { baseApi } from './api';
import { Project, ProjectListParams, ProjectListResponse, ApiResponse } from '../types';

// Build query string from params
const buildQueryString = (params?: ProjectListParams): string => {
  if (!params) return '';

  const queryParams = new URLSearchParams();

  if (params.page) queryParams.append('page', params.page.toString());
  if (params.limit) queryParams.append('limit', params.limit.toString());
  if (params.search && params.search.trim().length >= 2)
    queryParams.append('search', params.search);
  if (params.status && params.status !== 'all') queryParams.append('status', params.status);
  if (params.priority && params.priority !== 'all') queryParams.append('priority', params.priority);
  if (params.location) queryParams.append('location', params.location);
  if (params.managerId) queryParams.append('managerId', params.managerId);
  if (params.teamMember) queryParams.append('teamMember', params.teamMember);
  if (params.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
  if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
  if (params.dateTo) queryParams.append('dateTo', params.dateTo);
  if (params.budgetMin !== undefined) queryParams.append('budgetMin', params.budgetMin.toString());
  if (params.budgetMax !== undefined) queryParams.append('budgetMax', params.budgetMax.toString());
  if (params.tags && params.tags.length > 0) {
    params.tags.forEach(tag => queryParams.append('tags', tag));
  }

  const queryString = queryParams.toString();
  return queryString ? `?${queryString}` : '';
};

export const projectApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // Get all projects with caching
    getProjects: builder.query<ApiResponse<ProjectListResponse>, ProjectListParams | void>({
      query: params => `/projects${buildQueryString(params || undefined)}`,
      providesTags: result =>
        result?.success && result.data?.projects
          ? [
              ...result.data.projects.map(project => ({
                type: 'Project' as const,
                id: project._id,
              })),
              { type: 'Project', id: 'LIST' },
            ]
          : [{ type: 'Project', id: 'LIST' }],
      // Keep data cached for 5 minutes
      keepUnusedDataFor: 300,
    }),

    // Get single project by ID
    getProject: builder.query<ApiResponse<Project>, string>({
      query: id => `/projects/${id}`,
      providesTags: (result, error, id) => [{ type: 'Project', id }],
      keepUnusedDataFor: 300,
    }),

    // Get project overview
    getProjectOverview: builder.query<ApiResponse<any>, string>({
      query: id => `/projects/${id}/overview`,
      providesTags: (result, error, id) => [{ type: 'Project', id: `${id}-overview` }],
      keepUnusedDataFor: 60,
    }),

    // Create project
    createProject: builder.mutation<ApiResponse<Project>, any>({
      query: data => ({
        url: '/projects',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Project', id: 'LIST' }],
    }),

    // Update project
    updateProject: builder.mutation<ApiResponse<Project>, { id: string; data: Partial<Project> }>({
      query: ({ id, data }) => ({
        url: `/projects/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Project', id },
        { type: 'Project', id: 'LIST' },
      ],
    }),

    // Delete project
    deleteProject: builder.mutation<ApiResponse<void>, string>({
      query: id => ({
        url: `/projects/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Project', id },
        { type: 'Project', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetProjectsQuery,
  useGetProjectQuery,
  useGetProjectOverviewQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
} = projectApi;
